class CartDrawerRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItem = this.closest('.cart-item');
      // calls the assisted-wheels-upgrade component to update the quantity
      const wheelUpgradeVariantId = cartItem?.querySelector('assisted-wheels-upgrade')?.variantId;
      const cartDrawer = this.closest('cart-drawer');
      const variantToRemove = this.dataset.variant;
      
      // Add loading state
      if (cartItem) {
        cartItem.classList.add('cart-item--updating');
      }
      
      setTimeout(async () => {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        // Line KEYS, not 1-based indexes: /cart/change.js reorders the cart, so
        // an index computed here would point at the wrong line after the first
        // companion is removed (a gated parent can carry the Power Bank AND
        // the e-assist wheels as _free_gift lines).
        const freeGiftKeys = [];
        cart.items.forEach((item) => {
          if (item.properties &&
              item.properties._free_gift === 'true' &&
              String(item.properties._parent_variant_id) === String(variantToRemove)) {
            freeGiftKeys.push(item.key);
          }
        });

        for (const key of freeGiftKeys) {
          await fetch(`${routes.cart_change_url}`, {
            ...fetchConfig(),
            ...{
              body: JSON.stringify({
                id: key,
                quantity: 0
              })
            }
          });
        }
        
        // Remove wheel upgrade if exists. change.js by bare variant id is
        // mishandled by this store's cart transform and wipes other (pro/bundle)
        // line items, so remove by variant id through update.js instead.
        if (wheelUpgradeVariantId) {
          await fetch(`${routes.cart_update_url}`, {
            ...fetchConfig(),
            ...{ 
              body: JSON.stringify({
                updates: { [wheelUpgradeVariantId]: 0 }
              })
            }
          });
        }
        
        await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ 
            body: JSON.stringify({
              updates: { [variantToRemove]: 0 }
            })
          }
        });
        
        // Refresh sections once at the end
        const finalResponse = await fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            updates: {},
            sections: cartDrawer.getSectionsToRender().map((section) => section.section),
            sections_url: window.location.pathname
          })
        });
        const finalState = await finalResponse.json();
        
        cartDrawer.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            
          elementToReplace.innerHTML =
            cartDrawer.getSectionInnerHTML(finalState.sections[section.section], section.selector);
        }));
        
        window.updateSubtotalPrice();

        // Removing the capped preorder line frees up the cap - re-enable the
        // PDP ATC without a reload.
        if (typeof window.syncStoqPreorderCapState === 'function') {
          window.syncStoqPreorderCapState(finalState);
        }

        document.dispatchEvent(new CustomEvent('cart:ajax-updated', {
          detail: { line: variantToRemove, quantity: 0, parsedState: finalState, sections: cartDrawer.getSectionsToRender() }
        }));
      }, 1000);
    });
  }
}

customElements.define('cart-drawer-remove-button', CartDrawerRemoveButton);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Elements
    this.body = document.body;
    this.drawer = document.getElementById('cart-drawer');
    
    this.cartButton = document.querySelector('.js-open-cart');
    this.cartButton.addEventListener('click', this.handleCartClick.bind(this));

    this.closeIcons = document.querySelectorAll('.cart-drawer__close');
    this.closeIcons.forEach(closeIcon => {
      closeIcon.addEventListener('click', this.close.bind(this));
    });
    
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.drawer.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    this.pageOverlayElement = document.querySelector('.page-overlay');

    // Functionality
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce((event) => {
      if (!event.target.classList.contains('cart_upsell-variant-select')) {
        this.onChange(event);
      }
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  open() {
    this.drawer.setAttribute('aria-hidden', false);
    this.drawer.setAttribute('aria-expanded', true);

    this.pageOverlayElement.classList.add('is-visible');
    document.body.addEventListener('click', this.onBodyClick);

    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    this.body.style.top = `-${this.scrollPosition}px`;
    this.body.classList.add('scroll-lock');
    
    setTimeout(function(){
      window.updateSubtotalPrice();
    },500);
  }

  close(event) {
    if (event) {
      event.preventDefault();
    }

    this.drawer.setAttribute('aria-hidden', true);
    this.drawer.removeAttribute('aria-expanded', true);

    this.pageOverlayElement.classList.remove('is-visible');
    document.body.removeEventListener('click', this.onBodyClick);

    this.body.classList.remove('scroll-lock');
    this.body.style.top = '';
    
    if (this.scrollPosition !== undefined) {
      const previousHtmlScrollBehavior = document.documentElement.style.scrollBehavior;
      const previousBodyScrollBehavior = this.body.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      this.body.style.scrollBehavior = 'auto';
      window.scrollTo(0, this.scrollPosition);
      setTimeout(() => {
        document.documentElement.style.scrollBehavior = previousHtmlScrollBehavior;
        this.body.style.scrollBehavior = previousBodyScrollBehavior;
      }, 0);
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.variant, event.target.value, document.activeElement.getAttribute('name'));
  }

  async updateQuantity(variant, quantity, name) {
    this.enableLoading(variant);

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    const freeGiftLines = [];
    cart.items.forEach((item, index) => {
      if (item.properties && 
          item.properties._free_gift === 'true' && 
          String(item.properties._parent_variant_id) === String(variant)) {
        freeGiftLines.push(index + 1);
      }
    });

    // Clamp the line to the preorder cap (customizer limit / Stoq). Companion
    // (_free_gift) lines follow the same clamped quantity. No cap = no limit.
    const parentCap = typeof window.getStoqPreorderLimit === 'function'
      ? window.getStoqPreorderLimit(variant)
      : null;
    const targetQuantity = parentCap ? Math.min(parseInt(quantity, 10) || 0, parentCap) : parseInt(quantity, 10);

    // Update via update.js. change.js by bare variant id is mishandled by this
    // store's cart transform and wipes other (pro/bundle) line items, so key the
    // quantity change by variant id through update.js instead.
    const body = JSON.stringify({
      updates: { [variant]: targetQuantity },
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(`${routes.cart_update_url}`, {...fetchConfig(), ...{ body }});
      const state = await response.text();
      const parsedState = JSON.parse(state);

      for (const line of freeGiftLines) {
        await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(), 
          ...{ 
            body: JSON.stringify({
              line: line,
              quantity: targetQuantity,
              sections: this.getSectionsToRender().map((section) => section.section),
              sections_url: window.location.pathname
            })
          }
        });
      }

      const finalResponse = await fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          updates: {},
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        })
      });
      const finalState = await finalResponse.json();

      this.getSectionsToRender().forEach((section => {
        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          
        elementToReplace.innerHTML =
          this.getSectionInnerHTML(finalState.sections[section.section], section.selector);

      }));
    
      window.updateSubtotalPrice();

      // Re-check the preorder cap so the PDP ATC flips between "Out of Stock"
      // and preorder in real time as cart quantities change.
      if (typeof window.syncStoqPreorderCapState === 'function') {
        window.syncStoqPreorderCapState(finalState);
      }

      // Emit global cart update event after any AJAX cart operation
      document.dispatchEvent(new CustomEvent('cart:ajax-updated', {
        detail: { line: variant, quantity, parsedState: finalState, sections: this.getSectionsToRender() }
      }));

      this.disableLoading();
    } catch (error) {
      this.disableLoading();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer__content',
        section: document.getElementById('cart-drawer__content').dataset.id,
        selector: '.cart-drawer__content',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading() {
    // document.getElementById('cart-drawer-loading').classList.remove('hidden');
    // Dim the quantity selectors and disable pro-package add-ons while the cart updates
    this.classList.add('cart-drawer--updating');
  }

  disableLoading() {
    // document.getElementById('cart-drawer-loading').classList.add('hidden');
    this.classList.remove('cart-drawer--updating');
  }

  handleBodyClick(evt) {
    const target = evt.target;
    // console.log(evt)
    if (target.classList.contains('page-overlay')) {
      this.close();
    }
  }

  handleCartClick(evt) {
    evt.preventDefault();
    this.open();
  }
}

customElements.define('cart-drawer', CartDrawer);
